'use client';

import { c as _c } from "react/compiler-runtime";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, EffectFade, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import Image from "next/image";
import { useState, useEffect, useCallback } from 'react';
import LazyVideo from './LazyVideo';
import RichText from '@/components/RichText';
import styles from './grid.module.css';
import { CMSLink } from '@/components/Link';
import { motion } from 'motion/react';
// Import the alignment constants from nodeFormat
import { IS_ALIGN_LEFT, IS_ALIGN_CENTER, IS_ALIGN_RIGHT, IS_ALIGN_JUSTIFY, IS_ALIGN_START, IS_ALIGN_END } from '@/components/RichText/nodeFormat';
var Format = /*#__PURE__*/function (Format) {
  Format["Left"] = "left";
  Format["Start"] = "start";
  Format["Center"] = "center";
  Format["Right"] = "right";
  Format["End"] = "end";
  Format["Justify"] = "justify";
  return Format;
}(Format || {});
export const getJustifyClass = format => {
  // Handle both string and number formats
  switch (format) {
    // Handle string formats (from Lexical editor)
    case 'left':
    case 'start':
      return 'justify-start text-start';
    case 'center':
      return 'justify-center items-center text-center';
    case 'right':
    case 'end':
      return 'justify-end text-right';
    case 'justify':
      return 'justify-between text-justify';

    // Handle numeric formats (alignment constants)
    case IS_ALIGN_LEFT:
    case IS_ALIGN_START:
      return 'justify-start text-start';
    case IS_ALIGN_CENTER:
      return 'justify-center items-center text-center';
    case IS_ALIGN_RIGHT:
    case IS_ALIGN_END:
      return 'justify-end text-right';
    case IS_ALIGN_JUSTIFY:
      return 'justify-between text-justify';
    default:
      // Default alignment for slides without rich text content
      return 'justify-center items-center text-center';
  }
};
const SlideContent = t0 => {
  const $ = _c(47);
  const {
    slide,
    isMobile
  } = t0;
  const [showVideo, setShowVideo] = useState(false);
  const slideHasVideo = isMobile ? slide?.video_mobile : slide?.video_desktop;
  const format = slide?.richText?.root?.children?.[0]?.format;
  const t1 = format;
  let t2;
  if ($[0] !== t1) {
    t2 = getJustifyClass(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const justifyClass = t2;
  const hasContent = slide?.richText?.root?.children && slide.richText.root.children.length > 0 || slide?.enableLink && slide?.links && slide.links.length > 0;
  let t3;
  if ($[2] !== slide.richText?.root?.children?.[0]?.text) {
    t3 = () => slide?.richText?.root?.children?.[0]?.text || "Slider image";
    $[2] = slide.richText?.root?.children?.[0]?.text;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getAltText = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hidden: t4,
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.15,
          delayChildren: 0.3
        }
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const containerVariants = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 40
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [0.22, 1, 0.36, 1];
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      hidden: t6,
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.8,
          ease: t7
        }
      }
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const itemVariants = t8;
  let t10;
  let t9;
  if ($[9] !== slideHasVideo) {
    t9 = () => {
      if (slideHasVideo) {
        setShowVideo(true);
      }
    };
    t10 = [slideHasVideo];
    $[9] = slideHasVideo;
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  useEffect(t9, t10);
  const t11 = slide?.overlayOpacity ? slide.overlayOpacity / 100 : 0.5;
  let t12;
  if ($[12] !== t11) {
    t12 = <motion.div className="absolute top-0 w-screen h-full z-20 bg-background mix-blend-multiply" style={{
      opacity: t11
    }} />;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== hasContent || $[15] !== justifyClass || $[16] !== slide.enableLink || $[17] !== slide.gridPosition?.desktop?.colEnd || $[18] !== slide.gridPosition?.desktop?.colStart || $[19] !== slide.gridPosition?.desktop?.rowEnd || $[20] !== slide.gridPosition?.desktop?.rowStart || $[21] !== slide.gridPosition?.mobile?.colEnd || $[22] !== slide.gridPosition?.mobile?.colStart || $[23] !== slide.gridPosition?.mobile?.rowEnd || $[24] !== slide.gridPosition?.mobile?.rowStart || $[25] !== slide.gridPosition?.tablet?.colEnd || $[26] !== slide.gridPosition?.tablet?.colStart || $[27] !== slide.gridPosition?.tablet?.rowEnd || $[28] !== slide.gridPosition?.tablet?.rowStart || $[29] !== slide.links || $[30] !== slide.richText) {
    t13 = hasContent && <motion.div className={`flex flex-col justify-center ${styles.column}`} style={{
      "--col-start-mobile": slide?.gridPosition?.mobile?.colStart || 1,
      "--col-end-mobile": slide?.gridPosition?.mobile?.colEnd || 13,
      "--row-start-mobile": slide?.gridPosition?.mobile?.rowStart || 1,
      "--row-end-mobile": slide?.gridPosition?.mobile?.rowEnd || 2,
      "--col-start-tablet": slide?.gridPosition?.tablet?.colStart || 1,
      "--col-end-tablet": slide?.gridPosition?.tablet?.colEnd || 13,
      "--row-start-tablet": slide?.gridPosition?.tablet?.rowStart || 1,
      "--row-end-tablet": slide?.gridPosition?.tablet?.rowEnd || 2,
      "--col-start-desktop": slide?.gridPosition?.desktop?.colStart || 1,
      "--col-end-desktop": slide?.gridPosition?.desktop?.colEnd || 13,
      "--row-start-desktop": slide?.gridPosition?.desktop?.rowStart || 1,
      "--row-end-desktop": slide?.gridPosition?.desktop?.rowEnd || 2
    }} initial="hidden" animate="visible" variants={containerVariants}><div className={justifyClass}>{slide?.richText?.root?.children && slide.richText.root.children.length > 0 && <motion.div variants={itemVariants}><RichText data={slide?.richText ?? {
            root: {
              type: "root",
              children: [],
              direction: null,
              format: "",
              indent: 0,
              version: 1
            }
          }} enableProse={true} className={`prose-p:max-w-[500px] mb-4 prose-h3:text-2xl prose-h3:md:text-4xl text-gray-100 drop-shadow-sm`} /></motion.div>}{slide?.enableLink && slide?.links && slide.links.length > 0 && <motion.div className={`mt-4 flex gap-x-4 md:mt-4`} variants={itemVariants}>{(slide?.links || []).map(_temp)}</motion.div>}</div></motion.div>;
    $[14] = hasContent;
    $[15] = justifyClass;
    $[16] = slide.enableLink;
    $[17] = slide.gridPosition?.desktop?.colEnd;
    $[18] = slide.gridPosition?.desktop?.colStart;
    $[19] = slide.gridPosition?.desktop?.rowEnd;
    $[20] = slide.gridPosition?.desktop?.rowStart;
    $[21] = slide.gridPosition?.mobile?.colEnd;
    $[22] = slide.gridPosition?.mobile?.colStart;
    $[23] = slide.gridPosition?.mobile?.rowEnd;
    $[24] = slide.gridPosition?.mobile?.rowStart;
    $[25] = slide.gridPosition?.tablet?.colEnd;
    $[26] = slide.gridPosition?.tablet?.colStart;
    $[27] = slide.gridPosition?.tablet?.rowEnd;
    $[28] = slide.gridPosition?.tablet?.rowStart;
    $[29] = slide.links;
    $[30] = slide.richText;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <div className="absolute inset-0 z-30"><div className="container h-full"><div className="grid grid-cols-12 gap-4 h-full">{t13}</div></div></div>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== getAltText || $[35] !== isMobile || $[36] !== showVideo || $[37] !== slide.desktopImage || $[38] !== slide.mobileImage || $[39] !== slide.video_desktop || $[40] !== slide.video_mobile || $[41] !== slideHasVideo) {
    t15 = slideHasVideo && showVideo ? <motion.div className="absolute top-0 left-0 z-10 w-full h-full"><LazyVideo src={isMobile ? typeof slide.video_mobile === "object" && slide.video_mobile?.url ? slide.video_mobile.url : "" : typeof slide.video_desktop === "object" && slide.video_desktop?.url ? slide.video_desktop.url : ""} autoPlay={true} loop={true} muted={true} controls={false} className={`w-full h-full object-cover object-center ${isMobile ? "" : "hidden md:block"}`} playsInline={true} onLoadedData={() => setShowVideo(true)} /></motion.div> : <motion.div className="absolute top-0 left-0 w-full h-full z-10">{(typeof slide.desktopImage === "object" && slide.desktopImage?.url || typeof slide.mobileImage === "object" && slide.mobileImage?.url) && <>{typeof slide.desktopImage === "object" && slide.desktopImage?.url && <Image src={slide.desktopImage.url} alt={getAltText()} className="object-cover z-10 hidden md:block" fill={true} sizes="100vw" quality={100} priority={true} style={{
          objectPosition: "center",
          width: "100%",
          height: "100%"
        }} />}{typeof slide.mobileImage === "object" && slide.mobileImage?.url && <Image src={slide.mobileImage.url} alt={getAltText()} className="object-cover z-10 md:hidden" fill={true} sizes="100vw" quality={100} priority={true} style={{
          objectPosition: "center",
          width: "100%",
          height: "100%"
        }} />}</>}</motion.div>;
    $[34] = getAltText;
    $[35] = isMobile;
    $[36] = showVideo;
    $[37] = slide.desktopImage;
    $[38] = slide.mobileImage;
    $[39] = slide.video_desktop;
    $[40] = slide.video_mobile;
    $[41] = slideHasVideo;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t12 || $[44] !== t14 || $[45] !== t15) {
    t16 = <div className="w-full h-[85vh] relative">{t12}{t14}{t15}</div>;
    $[43] = t12;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  return t16;
};
export default function Slider(t0) {
  const $ = _c(13);
  const {
    locale,
    slides
  } = t0;
  const dir = locale === "ar" ? "rtl" : "ltr";
  const [isMobile, setIsMobile] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const updateIsMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener("resize", updateIsMobile);
      updateIsMobile();
      return () => {
        window.removeEventListener("resize", updateIsMobile);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== isMobile) {
    t3 = (slide, index) => <SwiperSlide key={index} data-theme="dark"><SlideContent slide={slide} isMobile={isMobile} /></SwiperSlide>;
    $[2] = isMobile;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const renderSlide = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      clickable: true
    };
    t5 = [Pagination, EffectFade, Navigation];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      crossFade: true
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== renderSlide || $[8] !== slides) {
    t7 = slides?.map(renderSlide);
    $[7] = renderSlide;
    $[8] = slides;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== dir || $[11] !== t7) {
    t8 = <div className={styles.heroSliderContainer}><Swiper spaceBetween={50} slidesPerView={1} loop={true} dir={dir} navigation={true} pagination={t4} modules={t5} effect="fade" fadeEffect={t6} className="hero-slider">{t7}</Swiper></div>;
    $[10] = dir;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function _temp(t0, i) {
  const {
    link
  } = t0;
  return <motion.div key={i} initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: 0.8 + i * 0.1,
      ease: [0.22, 1, 0.36, 1]
    }
  }}><CMSLink {...link} /></motion.div>;
}